import { Controller } from '@hotwired/stimulus';
import ChartDataLabels from 'chartjs-plugin-datalabels';

export default class DashboardChartsController extends Controller {
  static targets = [
    'termsCanvas',
    'overdueCanvas',
    'doughnutCanvas',
    'termsFilter',
    'doughnutFilter',
    'doughnutLegend'
  ];

  connect() {
    this.charts = {};
    this.initDoughnutChart();
    this.initTermsBarChart();
    this.initOverdueBarChart();

    if (this.hasTermsFilterTarget) {
      this.termsFilterTarget.addEventListener('change', this.onTermsScopeChange.bind(this));
    }

    if (this.hasDoughnutFilterTarget) {
      this.doughnutFilterTarget.addEventListener('change', this.onDoughnutScopeChange.bind(this));
    }
  }

  disconnect() {
    Object.values(this.charts).forEach(function(chart) {if (chart) {chart.destroy();}});
  }

  onTermsScopeChange() {
    if (this.charts.terms) {this.charts.terms.destroy();}
    this.initTermsBarChart();
  }

  onDoughnutScopeChange() {
    if (this.charts.doughnut) {this.charts.doughnut.destroy();}
    this.initDoughnutChart();
  }

  initDoughnutChart() {
    if (!this.hasDoughnutCanvasTarget) return;

    const canvas = this.doughnutCanvasTarget;
    const attribute = this.hasDoughnutFilterTarget ? this.doughnutFilterTarget.value : 'region';
    const datasetMap = {
      'region': 'chartDataRegion',
      'operation_type': 'chartDataOperationType',
      'project_owner_type': 'chartDataProjectOwnerType',
    };
    const data = JSON.parse(canvas.dataset[datasetMap[attribute] || 'chartDataRegion']);

    if (this.hasDoughnutLegendTarget) {
      this.doughnutLegendTargets.forEach(function(container) {container.innerHTML = '';});
    }

    if (!data.labels || data.labels.length === 0) {
      this.charts.doughnut = null;
      return;
    }

    const palette = [
      {bg: '#146090', text: '#FFFFFF'},
      {bg: '#F0BC81', text: '#262626'},
      {bg: '#3FAAE1', text: '#FFFFFF'},
      {bg: '#DFEEF8', text: '#262626'},
      {bg: '#EFEFEF', text: '#262626'},
    ];

    data.datasets[0].backgroundColor = palette.map(function(p) {return p.bg;});
    data.datasets[0].borderWidth = 0;
    data.datasets[0].hoverOffset = 0;

    this.charts.doughnut = new Chart(canvas, {
      type: 'doughnut',
      data: data,
      options: {
        maintainAspectRatio: false,
        cutout: '68%',
        responsive: false,
        plugins: {
          legend: {display: false},
          tooltip: {
            backgroundColor: '#ffffff',
            titleColor: '#262626',
            titleFont: {weight: 'bold', size: 14},
            bodyColor: '#262626',
            borderColor: '#E3F0FB',
            borderWidth: 1,
            cornerRadius: 8,
            padding: 12,
            usePointStyle: true,
            pointStyle: 'circle',
            boxWidth: 8,
            boxHeight: 8,
          },
          datalabels: {
            display: true,
            color: function(ctx) {return palette[ctx.dataIndex] ? palette[ctx.dataIndex].text : '#262626';},
            font: {weight: 'bold', size: 12},
            formatter: function(value, ctx) {
              const total = ctx.dataset.data.reduce(function(a, b) {return a + b;}, 0);
              const pct = Math.round(value / total * 100);
              return pct > 5 ? pct + '%' : '';
            }
          }
        }
      },
      plugins: [ChartDataLabels]
    });

    this.buildDoughnutLegend();
  }

  buildDoughnutLegend() {
    if (!this.hasDoughnutLegendTarget) return;

    const chart = this.charts.doughnut;
    const colors = chart.data.datasets[0].backgroundColor;

    this.doughnutLegendTargets.forEach(function(container) {
      container.innerHTML = '';
      chart.data.labels.forEach(function(label, i) {
        const item = document.createElement('div');
        item.className = 'd-flex align-items-center gap-2';

        const dot = document.createElement('span');
        dot.className = 'd-inline-block rounded-circle flex-shrink-0';
        dot.style.width = '8px';
        dot.style.height = '8px';
        dot.style.backgroundColor = colors[i] || '#EFEFEF';

        const text = document.createElement('span');
        text.className = 'fs-sm';
        text.textContent = label;

        item.appendChild(dot);
        item.appendChild(text);
        container.appendChild(item);
      });
    });
  }

  initTermsBarChart() {
    if (!this.hasTermsCanvasTarget) return;

    const canvas = this.termsCanvasTarget;
    const data = JSON.parse(canvas.dataset.chartData);
    const scope = this.hasTermsFilterTarget ? this.termsFilterTarget.value : 'all';

    if (scope && scope !== 'all') {
      const n = parseInt(scope);
      data.labels = data.labels.slice(0, n);
      data.datasets.forEach(function(ds) {
        ds.data = ds.data.slice(0, n);
        ds.description = ds.description.slice(0, n);
      });
    }

    this.charts.terms = new Chart(canvas, {
      type: 'bar',
      data: data,
      options: {
        maintainAspectRatio: false,
        responsive: true,
        scales: {
          x: {
            stacked: true,
            ticks: {maxRotation: 30, minRotation: 30, color: '#262626'},
            grid: {color: '#E6E6E7', borderDash: [4, 4]}},
          y: {
            stacked: true,
            grid: {color: '#E6E6E7', borderDash: [4, 4]},
            ticks: {
              color: '#262626',
              callback: function(label) {
                return label >= 1000 ? (label / 1000) + 'k' : label;
              }
            }
          }
        },
        plugins: {
          datalabels: {display: false},
          legend: {display: false},
          tooltip: {
            enabled: false,
            position: 'cursor',
            external: this.externalTooltipHandlerTerms.bind(this)
          }
        }
      },
      plugins: [ChartDataLabels]
    });
  }

  initOverdueBarChart() {
    if (!this.hasOverdueCanvasTarget) return;

    const canvas = this.overdueCanvasTarget;
    const data = JSON.parse(canvas.dataset.chartData);

    this.charts.overdue = new Chart(canvas, {
      type: 'bar',
      data: data,
      options: {
        barThickness: 20,
        maintainAspectRatio: false,
        responsive: true,
        scales: {
          x: {
            stacked: true,
            ticks: {maxRotation: 30, minRotation: 30},
            grid: {color: '#E6E6E7', borderDash: [4, 4]}
          },
          y: {
            stacked: true,
            grid: {color: '#E6E6E7', borderDash: [4, 4]},
            ticks: {
              callback: function(label) {
                return label >= 1000 ? (label / 1000) + 'k' : label;
              }
            }
          }
        },
        plugins: {
          datalabels: {display: false},
          legend: {display: false},
          tooltip: {
            enabled: false,
            position: 'cursor',
            external: this.externalTooltipHandler.bind(this)
          }
        }
      },
      plugins: [ChartDataLabels]
    });
  }

  getOrCreateTooltip(chart) {
    let tooltipEl = chart.canvas.parentNode.querySelector('div');

    if (!tooltipEl) {
      tooltipEl = document.createElement('div');
      tooltipEl.style.background = '#ffffff';
      tooltipEl.style.borderRadius = '8px';
      tooltipEl.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.08)';
      tooltipEl.style.color = '#262626';
      tooltipEl.style.opacity = 1;
      tooltipEl.style.pointerEvents = 'none';
      tooltipEl.style.position = 'absolute';
      tooltipEl.style.transform = 'translate(-40%, 0)';
      tooltipEl.style.transition = 'all .1s ease';
      tooltipEl.style.zIndex = '9999';
      tooltipEl.style.fontSize = '13px';
      tooltipEl.style.overflow = 'visible';
      tooltipEl.style.width = 'auto';
      tooltipEl.style.minWidth = '250px';
      tooltipEl.style.padding = '12px';

      const table = document.createElement('table');
      table.style.margin = '0px';
      tooltipEl.appendChild(table);
      chart.canvas.parentNode.appendChild(tooltipEl);
    }

    return tooltipEl;
  }

  buildTooltipContent(tooltipEl, title, rows) {
    const tableRoot = tooltipEl.querySelector('table');
    while (tableRoot.firstChild) {tableRoot.firstChild.remove();}

    const tableHead = document.createElement('thead');
    const titleTr = document.createElement('tr');
    const titleTd = document.createElement('td');
    titleTd.style.fontWeight = 'bold';
    titleTd.style.fontSize = '14px';
    titleTd.style.paddingBottom = '6px';
    titleTd.appendChild(document.createTextNode(title));
    titleTr.appendChild(titleTd);
    tableHead.appendChild(titleTr);

    const tableBody = document.createElement('tbody');
    rows.forEach(function(row) {
      const span = document.createElement('span');
      span.style.background = row.color;
      span.style.borderRadius = '50%';
      span.style.marginRight = '6px';
      span.style.height = '8px';
      span.style.width = '8px';
      span.style.display = 'inline-block';
      span.style.flexShrink = '0';

      const tr = document.createElement('tr');
      const td = document.createElement('td');
      td.style.borderWidth = 0;
      td.style.padding = 0;
      td.style.paddingBottom = '3px';
      td.style.display = 'flex';
      td.style.alignItems = 'center';
      td.appendChild(span);
      td.appendChild(document.createTextNode(row.text));
      tr.appendChild(td);
      tableBody.appendChild(tr);
    });

    tableRoot.appendChild(tableHead);
    tableRoot.appendChild(tableBody);
  }

  positionTooltip(tooltipEl, chart, tooltip) {
    tooltipEl.style.opacity = 1;
    tooltipEl.style.left = chart.canvas.offsetLeft + tooltip.caretX + 'px';
    tooltipEl.style.top = chart.canvas.offsetTop + tooltip.caretY + 'px';
  }

  externalTooltipHandlerTerms(context) {
    const chart = context.chart;
    const tooltip = context.tooltip;
    const tooltipEl = this.getOrCreateTooltip(chart);

    if (tooltip.opacity === 0) {tooltipEl.style.opacity = 0; return;}

    if (tooltip.body) {
      const parts = tooltip.dataPoints[0].label.split('/');
      const date = new Date(parseInt(parts[1]), parseInt(parts[0]) - 1, 1);
      const formatted = date.toLocaleDateString('fr-FR', {month: 'long', year: 'numeric'});
      const title = formatted.charAt(0).toUpperCase() + formatted.slice(1) + ' :';

      const rows = [];
      tooltip.dataPoints.forEach(function(dataPoint) {
        const desc = dataPoint.dataset.description[dataPoint.dataIndex];
        const datasetIndex = dataPoint.datasetIndex;
        const color = dataPoint.dataset.backgroundColor;

        Object.entries(desc).forEach(function(entry) {
          const val = Object.values(entry[1])[datasetIndex];
          if (val) {rows.push({text: entry[0] + ' : ' + val, color: color });}
        });
      });

      this.buildTooltipContent(tooltipEl, title, rows);
    }

    this.positionTooltip(tooltipEl, chart, tooltip);
  }

  externalTooltipHandler(context) {
    const chart = context.chart;
    const tooltip = context.tooltip;
    const tooltipEl = this.getOrCreateTooltip(chart);

    if (tooltip.opacity === 0) {tooltipEl.style.opacity = 0; return;}

    if (tooltip.body) {
      const dataPoint = tooltip.dataPoints[0];
      const contextIndex = dataPoint.datasetIndex;
      const currentContext = dataPoint.dataset.description;

      const rows = Object.entries(currentContext).reduce(function(formattedRows, projects) {
        const projectName = Object.keys(projects[1])[0];
        const value = Object.values(projects[1][projectName])[contextIndex];
        if (!!value && parseFloat(value) !== 0) {
          formattedRows.push({text: projectName + ' : ' + value, color: tooltip.labelColors[0].backgroundColor});
        }
        return formattedRows;
      }, []);

      this.buildTooltipContent(tooltipEl, tooltip.title[0] || '', rows);
    }

    this.positionTooltip(tooltipEl, chart, tooltip);
  }
}
